// Rental — Add / Edit / View sheet. View is the same form with every input
// disabled and no Save.
import * as React from "react";
import { Eye, X } from "lucide-react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "ui/dialog";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import { FieldRow } from "../../shared/form-section";
import { FileDropzone } from "../../shared/file-dropzone";
import AppConstant from "../../Globals/AppConstants";
import {
    FLOOR_OPTIONS,
    MONTH_OPTIONS,
    RENTAL_UPLOAD_TYPE,
    currentFinancialYearStart,
    financialYearLabel,
    isPdfFile,
    rentalFileUrl,
} from "./rentalConstants";

export type RentalFormMode = "add" | "edit" | "view";

export interface RentalFormSheetProps {
    open: boolean;
    mode: RentalFormMode;
    onClose: () => void;
    storeOptions: { value: string; label: string }[];
    formData: any;
    onChange: (patch: any) => void;
    /** Fires only when validation passes. */
    onSubmit: () => void;
}

type Errors = { storeId?: string; floor?: string; rentMonth?: string; financialYear?: string; paymentDate?: string; amount?: string };

// Whole-rupee amounts only — displayed comma-grouped (e.g. 1,00,000), no decimals.
const toDigits = (value: string) => value.replace(/[^0-9]/g, "");
const formatAmount = (value: any) => {
    const digits = toDigits(String(value ?? ""));
    return digits ? Number(digits).toLocaleString("en-IN") : "";
};

const TITLE: Record<RentalFormMode, string> = {
    add: "Add Rental",
    edit: "Edit Rental",
    view: "View Rental",
};

export function RentalFormSheet({
    open,
    mode,
    onClose,
    storeOptions,
    formData,
    onChange,
    onSubmit,
}: RentalFormSheetProps) {
    const [errors, setErrors] = React.useState<Errors>({});
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const readOnly = mode === "view";

    // Only the current financial year is offered; a rental saved against an
    // earlier one keeps its own year in the list so editing it doesn't blank it.
    const yearOptions = React.useMemo(() => {
        const starts = [currentFinancialYearStart()];
        const saved = Number(formData?.financialYear);
        if (saved && !starts.includes(saved)) starts.push(saved);
        return starts.map((s) => ({ value: String(s), label: financialYearLabel(s) }));
    }, [formData?.financialYear]);

    const clear = (key: keyof Errors) => setErrors((e) => ({ ...e, [key]: undefined }));

    const validate = (): boolean => {
        const next: Errors = {};
        if (!formData?.storeId) next.storeId = "Store is required";
        if (!formData?.floor) next.floor = "Floor is required";
        if (!formData?.rentMonth) next.rentMonth = "Month is required";
        if (!formData?.financialYear) next.financialYear = "Year is required";
        if (!formData?.paymentDate) next.paymentDate = "Date of Payment is required";
        if (!(Number(formData?.amount) > 0)) next.amount = "Enter a valid amount";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) onSubmit();
    };

    const close = () => {
        setErrors({});
        onClose();
    };

    const fileUuid = formData?.attachmentFileUuid;

    const openAttachment = () => {
        if (isPdfFile(fileUuid)) window.open(rentalFileUrl(fileUuid), "_blank", "noopener");
        else setPreviewOpen(true);
    };

    return (
        <Sheet open={open} onOpenChange={(o) => { if (!o) close(); }}>
            <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
                <SheetHeader className="border-b px-5 py-4">
                    <SheetTitle>{TITLE[mode]}</SheetTitle>
                </SheetHeader>
                <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
                    {formData?.rentalCode && (
                        <FieldRow label="Rental ID">
                            <p className="text-sm font-medium text-foreground">{formData.rentalCode}</p>
                        </FieldRow>
                    )}

                    <FieldRow label="Store" required error={errors.storeId}>
                        <Select
                            value={formData?.storeId ? String(formData.storeId) : undefined}
                            onValueChange={(v) => { onChange({ storeId: v }); clear("storeId"); }}
                            disabled={readOnly}
                        >
                            <SelectTrigger><SelectValue placeholder="Select store" /></SelectTrigger>
                            <SelectContent>
                                {storeOptions.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FieldRow>

                    <FieldRow label="Floor" required error={errors.floor}>
                        <Select
                            value={formData?.floor || undefined}
                            onValueChange={(v) => { onChange({ floor: v }); clear("floor"); }}
                            disabled={readOnly}
                        >
                            <SelectTrigger><SelectValue placeholder="Select floor" /></SelectTrigger>
                            <SelectContent>
                                {FLOOR_OPTIONS.map((f) => (
                                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </FieldRow>

                    <div className="grid grid-cols-2 gap-3">
                        <FieldRow label="Month" required error={errors.rentMonth}>
                            <Select
                                value={formData?.rentMonth ? String(formData.rentMonth) : undefined}
                                onValueChange={(v) => { onChange({ rentMonth: v }); clear("rentMonth"); }}
                                disabled={readOnly}
                            >
                                <SelectTrigger><SelectValue placeholder="Select month" /></SelectTrigger>
                                <SelectContent>
                                    {MONTH_OPTIONS.map((m) => (
                                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FieldRow>

                        <FieldRow label="Year" required error={errors.financialYear}>
                            <Select
                                value={formData?.financialYear ? String(formData.financialYear) : undefined}
                                onValueChange={(v) => { onChange({ financialYear: v }); clear("financialYear"); }}
                                disabled={readOnly}
                            >
                                <SelectTrigger><SelectValue placeholder="Select year" /></SelectTrigger>
                                <SelectContent>
                                    {yearOptions.map((y) => (
                                        <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FieldRow>
                    </div>

                    <FieldRow label="Date of Payment" required error={errors.paymentDate}>
                        <Input
                            type="date"
                            disabled={readOnly}
                            value={formData?.paymentDate ?? ""}
                            onChange={(e) => { onChange({ paymentDate: e.target.value }); clear("paymentDate"); }}
                        />
                    </FieldRow>

                    <FieldRow label="Amount" required error={errors.amount}>
                        <Input
                            type="text"
                            inputMode="numeric"
                            disabled={readOnly}
                            value={formatAmount(formData?.amount)}
                            onChange={(e) => { onChange({ amount: toDigits(e.target.value) }); clear("amount"); }}
                        />
                    </FieldRow>

                    <FieldRow label="Attachment (optional)">
                        {fileUuid ? (
                            <div className="flex items-center gap-3 rounded-lg border p-2">
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-foreground">
                                        {isPdfFile(fileUuid) ? "PDF uploaded" : "Image uploaded"}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={openAttachment}
                                    aria-label="View attachment"
                                    className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                >
                                    <Eye className="h-4 w-4" />
                                </button>
                                {!readOnly && (
                                    <button
                                        type="button"
                                        onClick={() => onChange({ attachmentFileUuid: null })}
                                        aria-label="Remove attachment"
                                        className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        ) : readOnly ? (
                            <p className="text-sm text-muted-foreground">No attachment</p>
                        ) : (
                            <FileDropzone
                                type={RENTAL_UPLOAD_TYPE}
                                accept=".png,.jpg,.jpeg,.pdf"
                                onUploaded={(file) => onChange({ attachmentFileUuid: file.fileUuid })}
                                hint="Receipt — image or PDF"
                            />
                        )}
                    </FieldRow>

                    <FieldRow label="Remarks">
                        <textarea
                            rows={3}
                            disabled={readOnly}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                            value={formData?.remarks ?? ""}
                            onChange={(e) => onChange({ remarks: e.target.value })}
                        />
                    </FieldRow>

                    <div className="mt-auto flex justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={close}>
                            {readOnly ? "Close" : AppConstant?.cancel}
                        </Button>
                        {!readOnly && <Button type="submit">{AppConstant?.save}</Button>}
                    </div>
                </form>
            </SheetContent>

            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogTitle className="sr-only">Rental Attachment</DialogTitle>
                    {fileUuid && !isPdfFile(fileUuid) && (
                        <img
                            src={rentalFileUrl(fileUuid)}
                            alt="Rental attachment"
                            className="max-h-[80vh] w-full rounded-lg object-contain"
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Sheet>
    );
}

export default RentalFormSheet;
